/**
 * Inspector for the **Approval** node: what the approver is being asked, and
 * who will be asked.
 *
 * The second half is the part worth building a custom inspector for. Who may
 * decide follows the pipeline's lifecycle state (ADR-0025), and that is not
 * something an operator can be expected to know — so the inspector says it, in
 * the state the pipeline is actually in, before the run rather than after it.
 */

import { Alert, Stack, Switch, Text, Textarea, TextInput } from "@mantine/core";

/** Mirrors the server's rule: the states whose gates need an Administrator are
 *  exactly the ones whose lifecycle entry would need an electronic signature. */
const ADMIN_GATED = ["validation", "production"];

export function ApprovalConfig({
  config,
  editable,
  onChange,
  lifecycleState,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  /** This pipeline's lifecycle state, which decides who may approve. */
  lifecycleState: string;
}) {
  const strict = ADMIN_GATED.includes(lifecycleState);

  return (
    <Stack gap="sm">
      <TextInput
        label="What are you asking?"
        description="Shown at the top of the approval. Write it as the question a person will answer."
        placeholder="Load the studies into Production?"
        value={String(config.title ?? "")}
        onChange={(e) => onChange("title", e.currentTarget.value)}
        disabled={!editable}
        maxLength={200}
      />
      <Textarea
        label="Anything they need to know"
        description="Optional. What to check before deciding — a report to read, a count to confirm."
        placeholder="Check the load report from the previous step: 4,312 products, 0 failures."
        value={String(config.instructions ?? "")}
        onChange={(e) => onChange("instructions", e.currentTarget.value)}
        disabled={!editable}
        autosize
        minRows={3}
        maxLength={4000}
      />
      <Switch
        label="Auto-approve"
        description="Pass this gate without asking — for scheduled runs. Each pass is still recorded and audited as auto-approved."
        checked={Boolean(config.auto_approve)}
        onChange={(e) => onChange("auto_approve", e.currentTarget.checked)}
        disabled={!editable}
      />
      {Boolean(config.auto_approve) && (
        <Alert color="yellow" variant="light">
          <Text size="sm">
            Nobody reviews the rows at this gate. Turning this on accepts what reaches it in advance
            — including AI-predicted values on their way to Vault (ADR-0040). Every pass is still
            recorded and audited.
          </Text>
        </Alert>
      )}
      <Alert color={strict ? "orange" : "blue"} variant="light" title="Who will be asked">
        {strict ? (
          <Text size="sm">
            This pipeline is in <b>{lifecycleState}</b>, so an <b>Administrator</b> must decide —
            and <b>not the person who started the run</b>. Same bar as promoting a pipeline
            (ADR-0017), for the same reason.
          </Text>
        ) : (
          <Text size="sm">
            This pipeline is in <b>development</b>, so any <b>Developer</b> may decide, including
            whoever started the run — otherwise you could not test the gate you just built. Promote
            the pipeline and it tightens to an Administrator, and to someone other than the runner.
          </Text>
        )}
      </Alert>
      <Text size="xs" c="dimmed">
        The run pauses here and holds nothing while it waits — no worker, no connection — so a
        migration parked overnight costs nothing until morning. Rows pass through the gate
        unchanged; a rejection stops the run and records why.
      </Text>
    </Stack>
  );
}
